// src/App.jsx — Admin-only app (admin.honestlee.app)
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { agentAPI } from './services/api';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';

// ── Dynamic tab title per route — distinguishes 7+ admin tabs at a glance ──
const ROUTE_TITLES = {
  '/': 'Admin Home', '/inbox': 'Unified Inbox', '/assignment': 'Agent Assignment', '/assignment-v2': 'Agent Assignment V2',
  '/bulk-upload': 'Bulk Photo Upload', '/local-photos': 'Local Photos Map', '/classification-prompt': 'AI Prompt Manager',
  '/media-manager': 'Media Manager', '/media-reclaim': 'Media Reclaim', '/venue-taxonomy/schema': 'Venue Taxonomy — Schema',
  '/venue-taxonomy/assign': 'Venue Taxonomy — Assign', '/venue-taxonomy/enrich-assign': 'Venue Taxonomy — Enrich',
  '/venue-taxonomy/mapping-rules': 'Venue Taxonomy — Rules', '/translation-cms': 'Translation CMS',
  '/venue-notes': 'Venue Notes', '/wifi-tests': 'WiFi Speed Tests', '/wifi-connections': 'WiFi Connections', '/wifi-gate-config': 'PWA WiFi Gate Config', '/asn-registry': 'ASN Registry',
  '/cron-overview': 'Cron Overview', '/system/code-stats': 'Code Stats', '/system/travelthru-plan': 'TravelThru Plan', '/users': 'User Manager', '/menu-manager': 'Digital Menu Manager',
  '/event-schema': 'Event Schema', '/venue-lifecycle': 'Venue Lifecycle', '/events-manager': 'Events Manager', '/event-intelligence': 'Event Intelligence',
  '/offers': 'Offers Manager', '/offer-types': 'Offers — Schema', '/offers/types': 'Offer Types', '/offers/modes': 'Redemption Modes', '/offers/type-redemption': 'Type × Redemption', '/offers/venue-matrix': 'Venue-Type Matrix', '/street-vendors': 'Street Vendor Manager', '/bi-dashboard': 'BI Dashboard',
  '/gamification': 'Gamification Dashboard', '/gamification-settings': 'Gamification Settings',
  '/legacy-roles': 'Legacy Roles', '/boundary-manager': 'Boundary Manager',
  '/bulk-pin-align': 'Bulk Pin Align',
  '/gps-change-log': 'GPS Change Log',
  '/whatsapp-intelligence': 'WhatsApp Intelligence', '/facebook-intelligence': 'Facebook Intelligence',
  '/google-intelligence': 'Google Intelligence', '/review-intelligence': 'Review Intelligence',
  '/group-discovery': 'Group Discovery', '/name-mention': 'Name Mention Resolver',
  '/visibility-audit': 'Visibility Audit', '/legacy-media-queue': 'Legacy Media Queue',
  '/menu-extraction': 'Menu Extraction', '/onboard': 'Venue Onboarding',
  '/admin-payments': 'Payments Dashboard',
  '/grab-scraper': 'Grab Scraper',
  '/dish-search': 'Dish Search',
  '/canonical-dishes': 'Canonical Dishes',
  '/canonical-dish-table': 'Canonical Dish Table',
  '/crm': 'CRM',
  '/dm-threads': 'DM Threads',
  '/unified-menus': 'Unified Menus',
  '/dish-stats': 'Dish Stats',
  '/grab-matching': 'Grab Matching',
  '/venue-grab-lookup': 'Venue → Grab Lookup',
  '/grab-media': 'Grab Media Manager',
  '/referrals': 'Referrals',
  '/monetization': 'Affiliate Partners',
  '/backlog': 'Backlog',
  '/campaign': 'Campaign Board — Travel Massive 2026',
  '/loyalty': 'Loyalty — Collect the Lees',
  '/loyalty/stickers': 'Lee Stamp Tree',
  '/loyalty/lees': 'Lees Catalog',
  '/loyalty/mappings': 'Lee Mappings',
  '/feature-parity': 'Feature Parity',
  '/image-matrix': 'Image Resize Matrix',
  '/zalo': 'Zalo CRM',
  '/activity-scraper': 'Activity Scraper',
  '/ssl-monitor': 'SSL Monitor',
  '/payment-rates': 'Payment Rates',
  '/facet-taxonomy': 'Facet Taxonomy',
  '/activities': 'Activities',
  '/carousel-config': 'Event Carousel Config', '/website-control': 'Website Control Panel', '/content': 'Content Studio', '/stats': 'Live Stats Registry', '/knowledge-base': 'Knowledge Base', '/knowledge-base-topics': 'KB Topics', '/contact-form': 'Contact Form Editor', '/hlee-scans': 'HLEE Scan Tracking',
  '/comms/facebook': 'Facebook Intelligence',
  '/comms/facebook/groups': 'Facebook Groups',
  '/comms/facebook/posts': 'Facebook Posts',
  '/comms/facebook/places': 'Facebook Places',
  '/comms/facebook/events': 'Facebook Events',
  '/comms/facebook/feed': 'Facebook Feed',
  '/comms/facebook/media': 'Facebook Media',
};
function RouteTitle() {
  const loc = useLocation();
  React.useEffect(() => {
    const path = loc.pathname;
    let label = ROUTE_TITLES[path];
    if (!label) {
      const match = Object.keys(ROUTE_TITLES).sort((a, b) => b.length - a.length)
        .find(key => path.startsWith(key) && key !== '/');
      label = match ? ROUTE_TITLES[match] : '';
    }
    document.title = label && label !== 'Admin Home' ? `${label} · HonestLee Admin` : 'HonestLee Admin';
  }, [loc.pathname]);
  return null;
}
import Login from './components/Login';
// CON-1610 — STATIC import, deliberately not lazy(): this renders above the
// login gate, outside any <Suspense> boundary, so a lazy chunk here would throw.
import LivePitchView from './components/admin/pitch/LivePitchView';
// CON-16xx — the private STATIC deck viewer. STATIC import, like LivePitchView:
// it renders above the login gate and must not depend on an authed chunk load.
import DeckShareView from './components/admin/pitch/DeckShareView';
// CON-1610 — public rehearsal playback. STATIC import, like LivePitchView: it must
// render above the login gate, so it cannot be behind a lazy() that needs the app shell.
import PitchRehearsalPlayer from './components/admin/pitch/PitchRehearsalPlayer';
import AdminLayout from './components/admin/AdminLayout';

// Lazy-load all admin screens
const AdminAssignmentDashboard = lazy(() => import('./components/AdminAssignmentDashboard'));
const NewsManager = lazy(() => import('./components/admin/NewsManager'));
const OnboardingDashboard = lazy(() => import('./components/OnboardingDashboard'));
const PlaceTriageScreen = lazy(() => import('./components/admin/PlaceTriageScreen'));
const BulkPhotoUploadScreen = lazy(() => import('./components/admin/BulkPhotoUploadScreen'));
const ClassificationPromptManager = lazy(() => import('./components/admin/ClassificationPromptManager'));
const MediaManagerScreen = lazy(() => import('./components/admin/MediaManagerScreen'));
const MediaReclaimScreen = lazy(() => import('./components/admin/MediaReclaimScreen'));
const QuickApproveScreen = lazy(() => import('./components/admin/QuickApproveScreen')); // v68.365 — mobile quick-approve
const AnalyticsScreen = lazy(() => import('./components/admin/AnalyticsScreen'));   // v68.372 — visitor analytics
const VisitsScreen = lazy(() => import('./components/admin/VisitsScreen'));         // v68.373 — visit history
const DriveIngestScreen = lazy(() => import('./components/admin/DriveIngestScreen')); // v68.371 — Drive ingest panel
const VendorConfigScreen = lazy(() => import('./components/admin/VendorConfigScreen')); // EV-1302 — mobile vendor settings
const VendorTrailsScreen = lazy(() => import('./components/admin/VendorTrailsScreen')); // AD-1407 — the trail MAP
const LocalHashSiftScreen = lazy(() => import('./components/admin/LocalHashSiftScreen'));
const LocalPhotosMapScreen = lazy(() => import('./components/admin/LocalPhotosMapScreen'));
const LocalAssignScreen    = lazy(() => import('./components/admin/LocalAssignScreen'));
const TranslationCMS = lazy(() => import('./components/admin/TranslationCMS'));
const VenueNotesManager    = lazy(() => import('./components/admin/VenueNotesManager'));
const WifiSpeedTestsScreen = lazy(() => import('./components/admin/WifiSpeedTestsScreen'));
const WifiConnectionsAdminScreen = lazy(() => import('./components/admin/WifiConnectionsAdminScreen'));
const WifiGateConfigScreen = lazy(() => import('./components/admin/WifiGateConfigScreen'));
const RelationTypesManager = lazy(() => import('./components/admin/RelationTypesManager')); // v68.194
const PaymentsHubScreen = lazy(() => import('./components/admin/PaymentsHubScreen')); // v68.xxx — merged Methods/Orders/Institutions tabbed hub
const AsnRegistryScreen    = lazy(() => import('./components/admin/AsnRegistryScreen'));
const CronOverviewScreen   = lazy(() => import('./components/admin/CronOverviewScreen'));
const OGSharePreviewScreen = lazy(() => import('./components/admin/OGSharePreviewScreen'));
const CodeStatsScreen      = lazy(() => import('./components/admin/CodeStatsScreen'));
const BusinessPlanScreen   = lazy(() => import('./components/admin/BusinessPlanScreen')); // v69.007 — TravelThru partnership plan viewer
// v66.161 (2026-05-18): audit log viewer for connectivity.wifi_network_audit_log
const WifiNetworksAuditScreen = lazy(() => import('./components/admin/WifiNetworksAuditScreen'));
// v66.165 (2026-05-20): WiFi signal heatmap viewer — reads RSSI samples from
// connectivity.wifi_signal_samples (populated by APK v67.146 collector).
const WifiSignalHeatmapScreen = lazy(() => import('./components/admin/WifiSignalHeatmapScreen'));
const UserManagerScreen    = lazy(() => import('./components/admin/UserManagerScreen'));
const PermissionsScreen    = lazy(() => import('./components/admin/PermissionsScreen'));   // FIELD-1626
const DigitalMenuManagerScreen = lazy(() => import('./components/admin/DigitalMenuManagerScreen'));
const MenuExtractionDetailScreen = lazy(() => import('./components/admin/MenuExtractionDetailScreen'));
const OffersManagerScreen    = lazy(() => import('./components/admin/OffersManagerScreen'));
const OfferSchemaHub         = lazy(() => import('./components/admin/OfferSchemaHub'));
const StreetVendorManagerScreen = lazy(() => import('./components/admin/StreetVendorManagerScreen'));
const BIDashboardScreen     = lazy(() => import('./components/admin/BIDashboardScreen'));
const LegacyRoleSwitcher    = lazy(() => import('./components/admin/LegacyRoleSwitcher'));
const AdminHomeDashboard   = lazy(() => import('./components/AdminHomeDashboard'));
const EventSchemaManager        = lazy(() => import('./components/admin/EventSchemaManager'));
const HleeQrManagerScreen       = lazy(() => import('./components/admin/HleeQrManagerScreen'));
const HleeCodesMegaTable        = lazy(() => import('./components/admin/HleeCodesMegaTable'));
const HleeScanTracking           = lazy(() => import('./components/admin/HleeScanTracking'));
const VenueLifecycleSchemaManager = lazy(() => import('./components/admin/VenueLifecycleSchemaManager'));
const EventsManagerScreen  = lazy(() => import('./components/admin/EventsManagerScreen'));
const EventOrganizersScreen = lazy(() => import('./components/admin/EventOrganizersScreen'));  // v68.434 EV-29
const ByWeekdayReviewScreen = lazy(() => import('./components/admin/ByWeekdayReviewScreen'));  // v68.303
const SocialComposerScreen  = lazy(() => import('./components/admin/SocialComposerScreen'));   // v68.314 M1
const HangoutsAdminScreen   = lazy(() => import('./components/admin/HangoutsAdminScreen'));    // EV-1448 — social hangouts moderation
const KbBrowserScreen       = lazy(() => import('./components/admin/KbBrowserScreen'));       // AD-1453 — KB markdown browser
const EventIntelligenceScreen = lazy(() => import('./components/admin/EventIntelligenceScreen'));
const AgentAssignmentV2     = lazy(() => import('./components/admin/AgentAssignmentV2'));
const BoundaryManagerScreen = lazy(() => import('./components/admin/BoundaryManagerScreen'));
const BulkPinAlignScreen    = lazy(() => import('./components/admin/BulkPinAlignScreen'));
const GpsChangeLogScreen    = lazy(() => import('./components/admin/GpsChangeLogScreen'));
const ZonesManagerScreen    = lazy(() => import('./components/admin/ZonesManagerScreen'));
const VenueTaxonomyHub       = lazy(() => import('./components/admin/VenueTaxonomyHub'));
const ReviewsManagerScreen   = lazy(() => import('./components/admin/ReviewsManagerScreen'));
const ReviewConfigScreen     = lazy(() => import('./components/admin/ReviewConfigScreen'));
const AgentVisitsAdminScreen = lazy(() => import('./components/admin/AgentVisitsAdminScreen'));
const WifiDisplaySchemaManager = lazy(() => import('./components/admin/WifiDisplaySchemaManager'));
const WifiNetworksAdminScreen  = lazy(() => import('./components/admin/WifiNetworksAdminScreen'));
const WifiOcrPromptsScreen     = lazy(() => import('./components/admin/WifiOcrPromptsScreen'));
const GrabScraperScreen        = lazy(() => import('./components/admin/GrabScraperScreen'));
const GrabMatchingScreen       = lazy(() => import('./components/admin/GrabMatchingScreen'));
const VenueGrabLookupScreen    = lazy(() => import('./components/admin/VenueGrabLookupScreen'));
const DishSearchScreen         = lazy(() => import('./components/admin/DishSearchScreen'));
const CanonicalDishesScreen    = lazy(() => import('./components/admin/CanonicalDishesScreen'));  // EV-528
const CanonicalDishTableScreen = lazy(() => import('./components/admin/CanonicalDishTableScreen'));  // EV-528 mega table
const CrmScreen = lazy(() => import('./components/admin/CrmScreen'));  // EV-1080 in-house CRM
const ZaloCrmScreen = lazy(() => import('./components/admin/ZaloCrmScreen'));  // EV-1209 Zalo CRM
const UnifiedInboxScreen = lazy(() => import('./components/admin/UnifiedInboxScreen'));  // EV-1231 unified inbox
const ActivityScraperScreen = lazy(() => import('./components/admin/ActivityScraperScreen'));  // EV-1198/1208 Activity Scraper
const DmThreadsScreen = lazy(() => import('./components/admin/DmThreadsScreen'));  // EV-1185 unified DM inbox
const UnifiedMenusScreen       = lazy(() => import('./components/admin/UnifiedMenusScreen'));        // EV-529 unified menu inventory
const DishStatsScreen          = lazy(() => import('./components/admin/DishStatsScreen'));
const GrabMediaManagerScreen   = lazy(() => import('./components/admin/GrabMediaManagerScreen'));
const MediaTypeSchemaManager   = lazy(() => import('./components/admin/MediaTypeSchemaManager'));
const DeployControlPanel       = lazy(() => import('./components/admin/DeployControlPanel'));
const ProspectingMapScreen     = lazy(() => import('./components/admin/ProspectingMapScreen'));
const VenueTableScreen         = lazy(() => import('./components/admin/VenueTableScreen'));
const VenueHealthScreen        = lazy(() => import('./components/admin/VenueHealthScreen'));
const VenueGoogleDataAdminScreen = lazy(() => import('./components/admin/VenueGoogleDataAdminScreen'));
const VenueGoogleEnrichmentScreen = lazy(() => import('./components/admin/VenueGoogleEnrichmentScreen'));
const AdminLineInboxScreen       = lazy(() => import('./components/admin/AdminLineInboxScreen'));
const AdminZaloInboxScreen       = lazy(() => import('./components/admin/AdminZaloInboxScreen'));
const RankingConfigScreen        = lazy(() => import('./components/admin/RankingConfigScreen'));
const CarouselConfigScreen       = lazy(() => import('./components/admin/CarouselConfigScreen'));
const WebsiteControlPanel        = lazy(() => import('./components/admin/WebsiteControlPanel')); // EV-1051
const ContentStudioScreen        = lazy(() => import('./components/admin/ContentStudioScreen'));  // CON-1566 content graph workbench
const StatsRegistryScreen        = lazy(() => import('./components/admin/StatsRegistryScreen'));  // CON-1589 live stats registry
const PitchDeckScreen            = lazy(() => import('./components/admin/PitchDeckScreen'));      // CON-1610 pitch deck studio
const PitchScriptScreen          = lazy(() => import('./components/admin/pitch/PitchScriptScreen')); // CON-1663 phone teleprompter
const ContactFormEditorScreen    = lazy(() => import('./components/admin/ContactFormEditorScreen')); // CON-1499b
const JobApplicationsScreen      = lazy(() => import('./components/admin/JobApplicationsScreen'));   // CON-1568 hiring board
const ApiConnectionsScreen       = lazy(() => import('./components/admin/ApiConnectionsScreen'));    // CON-1619 integration health
const KnowledgeBaseManager       = lazy(() => import('./components/admin/KnowledgeBaseManager')); // CON-1512
const KnowledgeBaseTopics        = lazy(() => import('./components/admin/KnowledgeBaseTopics')); // CON-1523
const NoiseAdminScreen           = lazy(() => import('./components/admin/NoiseAdminScreen'));
const FilterControlCenterScreen  = lazy(() => import('./components/admin/FilterControlCenterScreen'));
const VenueTypeAssignmentScreen  = lazy(() => import('./components/admin/VenueTypeAssignmentScreen'));
const VenueTypeAssignMapScreen   = lazy(() => import('./components/admin/VenueTypeAssignMapScreen')); // EV-687
const GooglePhotoBacklogScreen   = lazy(() => import('./components/admin/GooglePhotoBacklogScreen')); // EV-541
const CityGalleryConfigScreen    = lazy(() => import('./components/admin/CityGalleryConfigScreen')); // AD-784
const PlacesManagerScreen        = lazy(() => import('./components/admin/PlacesManagerScreen')); // AD-786
const VenueHoursEditor            = lazy(() => import('./components/admin/VenueHoursEditor'));
const MapDisplayConfigScreen     = lazy(() => import('./components/admin/MapDisplayConfigScreen'));
const EventSettingsScreen        = lazy(() => import('./components/admin/EventSettingsScreen'));   // EV-395
const VenueAddressScreen         = lazy(() => import('./components/admin/VenueAddressScreen'));
const GoogleReviewsScreen        = lazy(() => import('./components/admin/GoogleReviewsScreen'));
const ReferralsScreen            = lazy(() => import('./components/admin/ReferralsScreen'));
const BacklogScreen              = lazy(() => import('./components/admin/BacklogScreen'));
const CampaignBoardScreen        = lazy(() => import('./components/admin/CampaignBoardScreen')); // v69.947 — event/campaign kanban
const FeatureParityScreen        = lazy(() => import('./components/admin/FeatureParityScreen')); // EV-931 — cross-surface parity matrix
const ImageResizeMatrixScreen    = lazy(() => import('./components/admin/ImageResizeMatrixScreen')); // EV-1053 — cross-surface image-resize matrix
const SslMonitorScreen           = lazy(() => import('./components/admin/SslMonitorScreen')); // EV-1177 — SSL/TLS cert expiry monitor
const CommerceRatesScreen        = lazy(() => import('./components/admin/CommerceRatesScreen')); // EV-1160
const LoyaltyAdminScreen         = lazy(() => import('./components/admin/LoyaltyAdminScreen'));        // CON-525 — loyalty panel
const LoyaltyStickerTreeScreen   = lazy(() => import('./components/admin/LoyaltyStickerTreeScreen'));  // CON-525/#1068 — Lee stamp tree
const LeesCatalogScreen          = lazy(() => import('./components/admin/LeesCatalogScreen'));         // CON-525 — Notion Lee catalog + mappings
const LeeMappingsScreen          = lazy(() => import('./components/admin/LeeMappingsScreen'));         // CON-525 — Lee↔taxonomy matching table
const AtmManagerScreen           = lazy(() => import('./components/admin/AtmManagerScreen'));
// FinancialInstitutionsScreen + PaymentOrdersScreen are now rendered as tabs inside PaymentsHubScreen (v68.608 EV-199; merged)
const MatchScreeningsScreen      = lazy(() => import('./components/admin/MatchScreeningsScreen'));  // EV-45 — assign venues to World Cup matches
const CommsWhatsAppScreen        = lazy(() => import('./components/admin/CommsWhatsAppScreen'));
const WhatsAppGroupGraphScreen   = lazy(() => import('./components/admin/WhatsAppGroupGraphScreen')); // WA group shared-member force graph
const WhatsAppIntelligenceScreen = lazy(() => import('./components/admin/WhatsAppIntelligenceScreen'));
const FacebookIntelligenceScreen = lazy(() => import('./components/admin/FacebookIntelligenceScreen'));
const WhatsAppEventPipelineScreen = lazy(() => import('./components/admin/WhatsAppEventPipelineScreen'));
const WhatsAppContactsScreen     = lazy(() => import('./components/admin/WhatsAppContactsScreen'));
const WhatsAppGroupsScreen       = lazy(() => import('./components/admin/WhatsAppGroupsScreen'));
const WhatsAppMediaGalleryScreen = lazy(() => import('./components/admin/WhatsAppMediaGalleryScreen'));
const AffiliatePartnersScreen   = lazy(() => import('./components/admin/AffiliatePartnersScreen'));   // EV-489
const GamificationDashboardScreen = lazy(() => import('./components/admin/GamificationDashboardScreen')); // v68.975 EV-603
const GamificationSettingsScreen  = lazy(() => import('./components/admin/GamificationSettingsScreen'));  // v68.975 EV-603
const FacetTaxonomyScreen         = lazy(() => import('./components/admin/FacetTaxonomyScreen'));         // venue facet taxonomy (admin.facet_registry)
const ActivitiesScreen            = lazy(() => import('./components/admin/ActivitiesScreen'));            // EV-708 activities manager

const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a', color: '#94a3b8', fontSize: 16 }}>
    Loading...
  </div>
);

// Onboarding wrapper (admins can onboard venues too)
function OnboardingWrapper({ user, onLogout }) {
  const { tempVenueId } = useParams();
  const navigate = useNavigate();
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadVenueData = async () => {
      try {
        setLoading(true);
        const storedVenue = localStorage.getItem('selectedVenueForOnboarding');
        if (storedVenue) {
          try {
            const venue = JSON.parse(storedVenue);
            if (venue.tempVenueId === tempVenueId) {
              setSelectedVenue(venue);
              setLoading(false);
              return;
            }
          } catch (e) { /* ignore parse error */ }
        }
        const response = await agentAPI.getVenueByTempId(tempVenueId);
        if (response.data?.data) {
          localStorage.setItem('selectedVenueForOnboarding', JSON.stringify(response.data.data));
          setSelectedVenue(response.data.data);
        } else {
          throw new Error('Invalid venue data');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load venue data');
      } finally {
        setLoading(false);
      }
    };
    if (tempVenueId) loadVenueData();
    else { setError('No venue ID provided'); setLoading(false); }
  }, [tempVenueId]);

  if (loading) return <div style={styles.loadingContainer}><div style={styles.loadingSpinner}>Loading venue...</div></div>;
  if (error || !selectedVenue) return (
    <div style={styles.errorContainer}>
      <div style={styles.errorCard}>
        <h2 style={{ color: '#e53e3e', marginBottom: 20 }}>Venue Not Found</h2>
        <p style={{ color: '#4a5568', marginBottom: 15 }}>{error || 'Unable to load venue data'}</p>
        <button onClick={() => navigate('/')} style={styles.backButton}>Back to Dashboard</button>
      </div>
    </div>
  );

  return (
    <OnboardingDashboard
      user={user}
      selectedVenue={selectedVenue}
      onBack={() => { localStorage.removeItem('selectedVenueForOnboarding'); navigate('/'); }}
      onLogout={onLogout}
      onVenueUpdate={(v) => { setSelectedVenue(v); localStorage.setItem('selectedVenueForOnboarding', JSON.stringify(v)); }}
    />
  );
}

// Admin routes — all wrapped in AdminLayout
function AdminRoutes({ user, onLogout }) {
  const withLayout = (element) => (
    <AdminLayout user={user} onLogout={onLogout}>
      {element}
    </AdminLayout>
  );

  return (
    <><RouteTitle /><Routes>
      <Route path="/" element={withLayout(<AdminHomeDashboard user={user} onLogout={onLogout} />)} />
      <Route path="/assignment" element={<AdminAssignmentDashboard user={user} onLogout={onLogout} />} />
      <Route path="/onboard/:tempVenueId" element={<OnboardingWrapper user={user} onLogout={onLogout} />} />
      <Route path="/bulk-upload"            element={withLayout(<BulkPhotoUploadScreen user={user} onLogout={onLogout} />)} />
      <Route path="/classification-prompt"  element={withLayout(<ClassificationPromptManager user={user} onLogout={onLogout} />)} />
      <Route path="/media-manager"          element={withLayout(<MediaManagerScreen user={user} onLogout={onLogout} />)} />
      <Route path="/media-reclaim"          element={withLayout(<MediaReclaimScreen user={user} onLogout={onLogout} />)} />
      <Route path="/feature-parity"         element={withLayout(<FeatureParityScreen user={user} onLogout={onLogout} />)} />{/* EV-931 */}
      <Route path="/image-matrix"           element={withLayout(<ImageResizeMatrixScreen user={user} onLogout={onLogout} />)} />{/* EV-1053 */}
      <Route path="/ssl-monitor"            element={withLayout(<SslMonitorScreen user={user} onLogout={onLogout} />)} />{/* EV-1177 */}
      <Route path="/payment-rates"          element={withLayout(<CommerceRatesScreen user={user} onLogout={onLogout} />)} />{/* EV-1160 */}
      <Route path="/loyalty"                element={withLayout(<LoyaltyAdminScreen user={user} onLogout={onLogout} />)} />{/* CON-525 */}
      <Route path="/loyalty/stickers"       element={withLayout(<LoyaltyStickerTreeScreen user={user} onLogout={onLogout} />)} />{/* CON-525/#1068 */}
      <Route path="/loyalty/lees"           element={withLayout(<LeesCatalogScreen user={user} onLogout={onLogout} />)} />{/* CON-525 — Lees catalog */}
      <Route path="/loyalty/mappings"       element={withLayout(<LeeMappingsScreen user={user} onLogout={onLogout} />)} />{/* CON-525 — matching table */}
      <Route path="/quick-approve"          element={<QuickApproveScreen />} />{/* v68.365 — mobile, no desktop chrome */}
      <Route path="/analytics"              element={withLayout(<AnalyticsScreen />)} />{/* v68.372 */}
      <Route path="/visits"                 element={<Navigate to="/analytics" replace />} />{/* v68.850+ — merged into Analytics "Visit History" tab */}
      <Route path="/drive-ingest"           element={withLayout(<DriveIngestScreen />)} />{/* v68.371 */}
      <Route path="/local-hash-sift"        element={withLayout(<LocalHashSiftScreen />)} />
      <Route path="/local-photos"           element={withLayout(<LocalPhotosMapScreen />)} />
      <Route path="/local-assign"           element={<LocalAssignScreen />} />
      <Route path="/venue-taxonomy/schema"         element={withLayout(<VenueTaxonomyHub user={user} onLogout={onLogout} />)} />
      <Route path="/venue-taxonomy/assign"         element={withLayout(<VenueTaxonomyHub user={user} onLogout={onLogout} />)} />
      <Route path="/venue-taxonomy/enrich-assign"  element={withLayout(<VenueTaxonomyHub user={user} onLogout={onLogout} />)} />
      <Route path="/venue-taxonomy/mapping-rules"  element={withLayout(<VenueTaxonomyHub user={user} onLogout={onLogout} />)} />
      <Route path="/venue-taxonomy"         element={<Navigate to="/venue-taxonomy/schema" replace />} />
      <Route path="/venue-schema"           element={<Navigate to="/venue-taxonomy/schema" replace />} />
      <Route path="/translation-cms"        element={withLayout(<TranslationCMS user={user} onLogout={onLogout} />)} />
      <Route path="/venue-notes"            element={withLayout(<VenueNotesManager user={user} onLogout={onLogout} />)} />
      <Route path="/wifi-tests"             element={withLayout(<WifiSpeedTestsScreen user={user} onLogout={onLogout} />)} />
      <Route path="/wifi-connections"       element={withLayout(<WifiConnectionsAdminScreen user={user} />)} />
      <Route path="/wifi-gate-config"       element={withLayout(<WifiGateConfigScreen user={user} onLogout={onLogout} />)} />
      <Route path="/relation-types"         element={withLayout(<RelationTypesManager user={user} onLogout={onLogout} />)} />
      <Route path="/venue-relation-types"   element={<Navigate to="/relation-types" replace />} />
      <Route path="/admin-payments"         element={withLayout(<PaymentsHubScreen user={user} onLogout={onLogout} />)} />
      <Route path="/hlee-qr"                element={withLayout(<HleeQrManagerScreen user={user} onLogout={onLogout} />)} />
      <Route path="/hlee-codes-table"       element={withLayout(<HleeCodesMegaTable user={user} onLogout={onLogout} />)} />
      <Route path="/hlee-scans"             element={withLayout(<HleeScanTracking user={user} onLogout={onLogout} />)} />
      <Route path="/asn-registry"           element={withLayout(<AsnRegistryScreen    user={user} onLogout={onLogout} />)} />
      <Route path="/cron-overview"          element={withLayout(<CronOverviewScreen   user={user} onLogout={onLogout} />)} />
      <Route path="/system/og-templates"    element={withLayout(<OGSharePreviewScreen user={user} onLogout={onLogout} />)} />
      <Route path="/system/code-stats"      element={withLayout(<CodeStatsScreen      user={user} onLogout={onLogout} />)} />
      <Route path="/system/travelthru-plan" element={withLayout(<BusinessPlanScreen slug="travelthru" />)} />{/* v69.007 */}
      <Route path="/system/wifi-networks-audit" element={withLayout(<WifiNetworksAuditScreen user={user} onLogout={onLogout} />)} />
      <Route path="/system/wifi-signal-heatmap" element={withLayout(<WifiSignalHeatmapScreen user={user} onLogout={onLogout} />)} />
      <Route path="/users"                  element={withLayout(<UserManagerScreen user={user} onLogout={onLogout} />)} />
      <Route path="/permissions"            element={withLayout(<PermissionsScreen />)} />   {/* FIELD-1626 */}
      <Route path="/menu-manager"           element={withLayout(<DigitalMenuManagerScreen user={user} onLogout={onLogout} />)} />
      <Route path="/menu-extraction/:venueId" element={withLayout(<MenuExtractionDetailScreen />)} />
      <Route path="/event-schema"           element={withLayout(<EventSchemaManager user={user} onLogout={onLogout} />)} />
      {/* EV-614 — deep-linkable Field Designer tab + clean alias */}
      <Route path="/event-schema/:tab"      element={withLayout(<EventSchemaManager user={user} onLogout={onLogout} />)} />
      <Route path="/field-designer"         element={withLayout(<EventSchemaManager user={user} onLogout={onLogout} />)} />
      <Route path="/venue-lifecycle"        element={withLayout(<VenueLifecycleSchemaManager user={user} onLogout={onLogout} />)} />
      <Route path="/events-manager"         element={withLayout(<EventsManagerScreen user={user} onLogout={onLogout} />)} />
      <Route path="/match-screenings"       element={withLayout(<MatchScreeningsScreen user={user} onLogout={onLogout} />)} />{/* EV-45 — World Cup venue screenings */}
      <Route path="/event-organizers"       element={withLayout(<EventOrganizersScreen user={user} onLogout={onLogout} />)} />{/* v68.434 EV-29 */}
      <Route path="/by-weekday-review"      element={withLayout(<ByWeekdayReviewScreen user={user} onLogout={onLogout} />)} />{/* v68.303 */}
      <Route path="/social-composer"        element={withLayout(<SocialComposerScreen user={user} onLogout={onLogout} />)} />{/* v68.314 */}
      <Route path="/hangouts"               element={withLayout(<HangoutsAdminScreen user={user} onLogout={onLogout} />)} />{/* EV-1448 */}
      <Route path="/kb"                     element={withLayout(<KbBrowserScreen user={user} onLogout={onLogout} />)} />{/* AD-1453 */}
      <Route path="/event-intelligence"     element={withLayout(<EventIntelligenceScreen user={user} onLogout={onLogout} />)} />
      <Route path="/offers"                element={withLayout(<OffersManagerScreen />)} />
      <Route path="/offer-types"          element={withLayout(<OfferSchemaHub />)} />
      <Route path="/offers/types"           element={withLayout(<OfferSchemaHub />)} />
      <Route path="/offers/modes"           element={withLayout(<OfferSchemaHub />)} />
      <Route path="/offers/type-redemption" element={withLayout(<OfferSchemaHub />)} />
      <Route path="/offers/venue-matrix"    element={withLayout(<OfferSchemaHub />)} />
      <Route path="/street-vendors"        element={withLayout(<StreetVendorManagerScreen />)} />
      <Route path="/bi-dashboard"          element={withLayout(<BIDashboardScreen />)} />
      <Route path="/legacy-roles"          element={withLayout(<LegacyRoleSwitcher />)} />
      <Route path="/assignment-v2"         element={withLayout(<AgentAssignmentV2 />)} />
      <Route path="/boundary-manager"     element={withLayout(<BoundaryManagerScreen />)} />
      <Route path="/bulk-pin-align"       element={withLayout(<BulkPinAlignScreen />)} />
      <Route path="/gps-change-log"       element={withLayout(<GpsChangeLogScreen />)} />
      <Route path="/zones-manager"        element={withLayout(<ZonesManagerScreen />)} />
      <Route path="/venue-type-manager"   element={<Navigate to="/venue-taxonomy/assign" replace />} />
      <Route path="/reviews-manager"      element={withLayout(<ReviewsManagerScreen user={user} onLogout={onLogout} />)} />
      <Route path="/review-config"        element={withLayout(<ReviewConfigScreen />)} />
      <Route path="/agent-visits"         element={withLayout(<AgentVisitsAdminScreen />)} />
      <Route path="/wifi-networks"        element={withLayout(<WifiNetworksAdminScreen user={user} />)} />
      <Route path="/wifi-display-schema"  element={withLayout(<WifiDisplaySchemaManager user={user} />)} />
      <Route path="/wifi-ocr-prompts"     element={withLayout(<WifiOcrPromptsScreen />)} />
      <Route path="/google-reviews"       element={withLayout(<GoogleReviewsScreen user={user} />)} />
      <Route path="/referrals"            element={withLayout(<ReferralsScreen user={user} />)} />
      <Route path="/monetization"         element={withLayout(<AffiliatePartnersScreen />)} />{/* EV-489 — affiliate partners + revenue */}
      <Route path="/gamification"          element={withLayout(<GamificationDashboardScreen />)} />{/* v68.975 EV-603 — gamification dashboard */}
      <Route path="/gamification-settings" element={withLayout(<GamificationSettingsScreen />)} />{/* v68.975 EV-603 — gamification settings */}
      <Route path="/backlog"              element={withLayout(<BacklogScreen user={user} />)} />
      <Route path="/campaign"             element={withLayout(<CampaignBoardScreen user={user} />)} />{/* v69.947 — Travel Massive 2026 board */}
      <Route path="/facet-taxonomy"       element={withLayout(<FacetTaxonomyScreen user={user} onLogout={onLogout} />)} />
      <Route path="/activities"           element={withLayout(<ActivitiesScreen user={user} onLogout={onLogout} />)} />
      <Route path="/atms"                 element={withLayout(<AtmManagerScreen user={user} />)} />
      <Route path="/institutions"         element={<Navigate to="/admin-payments?tab=institutions" replace />} />{/* v68.608 EV-199 → merged into Payments hub */}
      <Route path="/payments-orders"      element={<Navigate to="/admin-payments?tab=orders" replace />} />{/* v68.608 EV-199 → merged into Payments hub */}
      <Route path="/grab-scraper"         element={withLayout(<GrabScraperScreen />)} />
      <Route path="/grab-matching"        element={withLayout(<GrabMatchingScreen />)} />
      <Route path="/venue-grab-lookup"    element={withLayout(<VenueGrabLookupScreen />)} />
      <Route path="/dish-search"          element={withLayout(<DishSearchScreen />)} />
      <Route path="/canonical-dishes"     element={withLayout(<CanonicalDishesScreen />)} />{/* EV-528 */}
      <Route path="/canonical-dish-table" element={withLayout(<CanonicalDishTableScreen />)} />{/* EV-528 mega table */}
      <Route path="/crm" element={withLayout(<CrmScreen />)} />{/* EV-1080 in-house CRM */}
      <Route path="/zalo" element={withLayout(<ZaloCrmScreen />)} />{/* EV-1209 Zalo CRM */}
      <Route path="/activity-scraper" element={withLayout(<ActivityScraperScreen />)} />{/* EV-1198/1208 Activity Scraper */}
      <Route path="/dm-threads" element={<Navigate to="/inbox?channel=whatsapp" replace />} />{/* EV-1237 — retired into the Unified Inbox */}
      <Route path="/inbox" element={withLayout(<UnifiedInboxScreen />)} />{/* EV-1231 unified inbox across all channels */}
      <Route path="/place-triage" element={withLayout(<PlaceTriageScreen />)} />{/* v70.456 WhatsApp place-mention triage */}
      <Route path="/crm/contact/:contactId" element={withLayout(<CrmScreen />)} />{/* EV-1132 deep-link */}
      <Route path="/crm/organization/:orgId" element={withLayout(<CrmScreen />)} />{/* EV-1188 full company page (own tab) */}
      <Route path="/unified-menus"        element={withLayout(<UnifiedMenusScreen />)} />{/* EV-529 unified menu inventory */}
      <Route path="/dish-stats"           element={withLayout(<DishStatsScreen />)} />
      <Route path="/grab-media"           element={withLayout(<GrabMediaManagerScreen />)} />
      <Route path="/media-type-schema"    element={withLayout(<MediaTypeSchemaManager />)} />
      <Route path="/deploy"              element={withLayout(<DeployControlPanel />)} />
      <Route path="/prospecting"         element={withLayout(<ProspectingMapScreen user={user} />)} />
      <Route path="/venue-table"         element={withLayout(<VenueTableScreen />)} />
      <Route path="/vendor-config"       element={withLayout(<VendorConfigScreen />)} />{/* EV-1302 */}
      <Route path="/vendor-trails"       element={withLayout(<VendorTrailsScreen />)} />{/* AD-1407 — map */}
      <Route path="/venue-health"        element={withLayout(<VenueHealthScreen />)} />
      <Route path="/venue-google-data"   element={withLayout(<VenueGoogleDataAdminScreen user={user} />)} />
      <Route path="/venue-google-enrichment" element={withLayout(<VenueGoogleEnrichmentScreen />)} />
      <Route path="/admin/line-inbox"  element={withLayout(<AdminLineInboxScreen />)} />
      <Route path="/admin/zalo-inbox"  element={withLayout(<AdminZaloInboxScreen />)} />
      <Route path="/comms/whatsapp"         element={withLayout(<WhatsAppIntelligenceScreen />)} />
      <Route path="/comms/whatsapp-monitor"  element={withLayout(<CommsWhatsAppScreen />)} />
      <Route path="/comms/whatsapp-events"   element={withLayout(<WhatsAppEventPipelineScreen />)} />
      <Route path="/comms/whatsapp-contacts" element={withLayout(<WhatsAppContactsScreen />)} />
      <Route path="/comms/whatsapp-groups"   element={withLayout(<WhatsAppGroupsScreen />)} />
      <Route path="/whatsapp/graph"          element={withLayout(<WhatsAppGroupGraphScreen />)} />
      <Route path="/comms/whatsapp-media"    element={withLayout(<WhatsAppMediaGalleryScreen />)} />
      <Route path="/comms/facebook"          element={withLayout(<FacebookIntelligenceScreen />)} />
      <Route path="/comms/facebook-groups"   element={<Navigate to="/comms/facebook/groups" replace />} />
      <Route path="/comms/facebook/groups"   element={withLayout(<FacebookIntelligenceScreen initialTab="groups" />)} />
      <Route path="/comms/facebook/posts"    element={withLayout(<FacebookIntelligenceScreen initialTab="posts" />)} />
      <Route path="/comms/facebook/places"   element={withLayout(<FacebookIntelligenceScreen initialTab="places" />)} />
      <Route path="/comms/facebook/events"   element={withLayout(<FacebookIntelligenceScreen initialTab="events" />)} />
      <Route path="/comms/facebook/feed"     element={withLayout(<FacebookIntelligenceScreen initialTab="feed" />)} />
      <Route path="/comms/facebook/media"    element={withLayout(<FacebookIntelligenceScreen initialTab="media" />)} />
      <Route path="/create-event" element={withLayout(
        <div style={{ height: 'calc(100vh - 48px)', margin: '-24px', overflow: 'hidden' }}>
          <iframe src="https://thai.honestlee.app/create-event" style={{ width: '100%', height: '100%', border: 'none' }} title="Create Event" />
        </div>
      )} />
      <Route path="/ranking-config"    element={withLayout(<RankingConfigScreen />)} />
      <Route path="/carousel-config"   element={withLayout(<CarouselConfigScreen />)} />
      <Route path="/website-control"   element={withLayout(<WebsiteControlPanel />)} />
      <Route path="/content"           element={withLayout(<ContentStudioScreen />)} />{/* CON-1566 */}
      <Route path="/stats"             element={withLayout(<StatsRegistryScreen />)} />{/* CON-1589 */}
      {/* CON-1610 — no AdminLayout chrome: rehearse/audience modes go full-bleed,
          and a sidebar on a phone during a rehearsal is exactly the wrong thing. */}
      <Route path="/pitch"             element={<PitchDeckScreen />} />
      <Route path="/pitch-script"      element={<PitchScriptScreen />} />

      <Route path="/contact-form"      element={withLayout(<ContactFormEditorScreen />)} />
      <Route path="/job-applications"  element={withLayout(<JobApplicationsScreen />)} />{/* CON-1568 — /jobs applications + AI rating */}
      <Route path="/api-connections"   element={withLayout(<ApiConnectionsScreen />)} />{/* CON-1619 — integration health, approvals, templates, calendar */}
      <Route path="/knowledge-base"    element={withLayout(<KnowledgeBaseManager />)} />
      <Route path="/knowledge-base-topics" element={withLayout(<KnowledgeBaseTopics />)} />
      <Route path="/news" element={<NewsManager />} />
      <Route path="/noise-admin"       element={withLayout(<NoiseAdminScreen />)} />
      <Route path="/filter-control-center" element={withLayout(<FilterControlCenterScreen />)} />
      <Route path="/venue-type-assignment" element={withLayout(<VenueTypeAssignmentScreen />)} />
      <Route path="/venue-type-assign-map" element={withLayout(<VenueTypeAssignMapScreen />)} />
      <Route path="/google-photo-backlog" element={withLayout(<GooglePhotoBacklogScreen />)} />
      <Route path="/city-gallery-config"  element={withLayout(<CityGalleryConfigScreen />)} />{/* AD-784 */}
      <Route path="/places-manager"       element={withLayout(<PlacesManagerScreen />)} />{/* AD-786 */}
      <Route path="/venue-hours"          element={withLayout(<VenueHoursEditor />)} />
      <Route path="/map-settings"        element={withLayout(<MapDisplayConfigScreen />)} />
      <Route path="/event-settings"      element={withLayout(<EventSettingsScreen />)} />
      <Route path="/venue-addresses"    element={withLayout(<VenueAddressScreen user={user} onLogout={onLogout} />)} />
      {/* Field routes — link out to field.honestlee.app */}
      <Route path="/field" element={withLayout(<FieldRedirect />)} />
      <Route path="/field/*" element={withLayout(<FieldRedirect />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes></>
  );
}

// Redirect component for field routes
function FieldRedirect() {
  const fieldUrl = window.location.hostname === 'localhost'
    ? 'http://localhost:3003/field'
    : 'https://field.honestlee.app/field';
  return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <h2 style={{ color: '#e2e8f0', marginBottom: 16 }}>Field Agent Tools</h2>
      <p style={{ color: '#94a3b8', marginBottom: 24 }}>Field tools have moved to a dedicated app for better performance on mobile.</p>
      <a href={fieldUrl} target="_blank" rel="noopener noreferrer"
        style={{ background: '#3b82f6', color: 'white', padding: '12px 24px', borderRadius: 10, textDecoration: 'none', fontWeight: 600 }}>
        Open Field App
      </a>
    </div>
  );
}

// Root App component
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // CON-1499 / AN-1498 — publish the cross-property identity HINT on EVERY load,
  // not only inside the saved-session branch. Patrick 2026-08-03: "I'm logged
  // into another browser right now as me, as the admin, and it still doesn't
  // recognize me. It should prefill the email and name in that form."
  // The first version hooked one code path and simply never fired for his login
  // route. This runs unconditionally whenever the localStorage user keys exist.
  // NOT auth: no token, grants nothing, the server never trusts it — it exists
  // so honestlee.app (which cannot read this origin's localStorage) can greet
  // and prefill. Cleared when the keys are gone.
  useEffect(() => {
    try {
      const host = (window.location.hostname || '').toLowerCase();
      if (host !== 'honestlee.app' && !host.endsWith('.honestlee.app')) return;
      const base = '; path=/; SameSite=Lax; domain=.honestlee.app';
      const id = localStorage.getItem('user_id');
      const email = localStorage.getItem('user_email');
      const name = localStorage.getItem('user_name');
      if (!id && !email) { document.cookie = `hl_who=${base}; max-age=0`; return; }
      const payload = JSON.stringify({
        id: id || undefined, name: name || undefined, email: email || undefined,
        phone: localStorage.getItem('user_phone') || undefined,
      });
      document.cookie = `hl_who=${encodeURIComponent(payload)}${base}; max-age=${60 * 60 * 24 * 180}`;
      // CON-1512 (option B) — publish the REAL JWT domain-wide so honestlee.app
      // can prove admin-ness and be shown UNPUBLISHED knowledge-base drafts.
      // The website never decides this itself: the backend VERIFIES the
      // signature with JWT_SECRET, so a forged cookie simply fails. Session
      // lifetime only (max-age omitted) — it dies with the browser session.
      // 🔴 CON-1535b — GIVE IT A LIFETIME. This was a SESSION cookie (no
      // max-age), so it died the moment the browser closed while hl_who lived
      // 180 days. Patrick's name therefore showed on honestlee.app while his
      // ADMIN status silently did not — "it says you're logged in as Patrick
      // Oskar but you're not logged in as admin, patrick@honestlee.app IS an
      // admin". He was right; the cookie had simply expired.
      //
      // 12 hours, and safe: the backend VERIFIES the JWT signature and its own
      // expiry on every request, so a stale cookie is rejected server-side. The
      // cookie is not the authority, the signature is.
      const tok = localStorage.getItem('token');
      if (tok) document.cookie = `hl_sess=${encodeURIComponent(tok)}${base}; max-age=${60 * 60 * 12}`;
    } catch (e) { /* cookies blocked — prefill just won't happen */ }
  });

  useEffect(() => {
    // Local dev auto-login bypass
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isDevBypass = isLocalhost && (process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV_MODE === 'true');
    if (isDevBypass) {
      const devToken = localStorage.getItem('token');
      if (!devToken) {
        const mockUser = {
          _id: 'dev-user-admin',
          userId: 'dev-user-admin',
          name: 'Dev Admin',
          email: 'dev@honestlee.com',
          role: 'ADMIN',
          region: 'vn',
          loginMethod: 'dev-bypass'
        };
        localStorage.setItem('token', 'dev-mock-token-admin-' + Date.now());
        localStorage.setItem('user_id', mockUser.userId);
        localStorage.setItem('user_role', mockUser.role);
        localStorage.setItem('user_region', mockUser.region);
        localStorage.setItem('user_email', mockUser.email);
        localStorage.setItem('user_phone', '');
        localStorage.setItem('user_name', mockUser.name);
        localStorage.setItem('user_login_method', mockUser.loginMethod);
        setUser(mockUser);
        setIsLoggedIn(true);
        setLoading(false);
        return;
      }
    }

    // Check for saved authentication
    const savedToken = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');
    if (savedToken && userId) {
      try {
        const userData = {
          _id: userId,
          userId: userId,
          name: localStorage.getItem('user_name') || '',
          email: localStorage.getItem('user_email') || '',
          phone: localStorage.getItem('user_phone') || '',
          role: localStorage.getItem('user_role') || 'ADMIN',
          region: localStorage.getItem('user_region') || 'vn',
          loginMethod: localStorage.getItem('user_login_method') || ''
        };
        // CON-1499 / AN-1498 — publish a cross-property identity HINT so the
        // marketing sites can greet + prefill. Not auth: no token, grants
        // nothing, server never trusts it. honestlee.app cannot read this
        // origin's localStorage, so a .honestlee.app cookie is the only channel.
        try {
          const _who = JSON.stringify({ id: userId, name: userData.name, email: userData.email, phone: userData.phone });
          document.cookie = 'hl_who=' + encodeURIComponent(_who)
            + '; path=/; max-age=' + (60 * 60 * 24 * 180) + '; SameSite=Lax; domain=.honestlee.app';
        } catch (e) { /* cookies blocked — prefill just won't happen */ }
        setUser(userData);
        setIsLoggedIn(true);
      } catch (e) {
        handleLogout();
      }
    }
    setLoading(false);
  }, []);

  const handleLoginSuccess = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user_id', userData.userId || userData._id || '');
    localStorage.setItem('user_role', userData.role || '');
    localStorage.setItem('user_region', userData.region || 'vn');
    localStorage.setItem('user_email', userData.email || '');
    localStorage.setItem('user_phone', userData.phone || '');
    localStorage.setItem('user_name', userData.name || '');
    localStorage.setItem('user_login_method', userData.loginMethod || '');
    setUser(userData);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    ['token', 'user_id', 'user_role', 'user_region', 'user_email', 'user_phone',
     'user_name', 'user_login_method', 'selectedVenueForOnboarding'].forEach(k => localStorage.removeItem(k));
    setUser(null);
    setIsLoggedIn(false);
  };

  // CON-1610 — the audience screen, BEFORE the login gate and before the loading
  // state. The whole admin app requires an ADMIN account, so a tablet or a
  // second laptop could never show the deck without one. This route is guarded
  // by the deck's secret token instead, and the server hands it published slides
  // only — no editor, no reserve slides, no speaker notes.
  // Checked on pathname rather than through the router because BrowserRouter is
  // mounted further down, inside the authenticated branch.
  if (window.location.pathname === '/pitch-live') return <LivePitchView />;
  // /deck/<64-hex token> — a private, self-navigated share link. Above the gate
  // by design: the token IS the credential, exactly like /pitch-live.
  if (/^\/deck\/[a-f0-9]{32,}$/i.test(window.location.pathname)) return <DeckShareView />;
  if (window.location.pathname === '/pitch-rehearsal') return <PitchRehearsalPlayer />;

  if (loading) return <div style={styles.loadingContainer}><p style={{ color: 'white', fontSize: 18 }}>Initializing...</p></div>;
  if (!isLoggedIn || !user) return <Login onLoginSuccess={handleLoginSuccess} />;

  // Non-admin users get a message pointing to field app
  if (user.role !== 'ADMIN') {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <h2 style={{ color: '#e53e3e', marginBottom: 20 }}>Admin Access Required</h2>
          <p style={{ color: '#4a5568', marginBottom: 15 }}>
            This app is for admin users only. Field agents should use the Field App.
          </p>
          <a href="https://field.honestlee.app" style={styles.backButton}>Go to Field App</a>
          <button onClick={handleLogout} style={{ ...styles.backButton, background: '#f56565', marginLeft: 10 }}>Logout</button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Suspense fallback={<PageLoader />}>
        <AdminRoutes user={user} onLogout={handleLogout} />
      </Suspense>
    </BrowserRouter>
  );
}

const styles = {
  loadingContainer: {
    minHeight: '100vh', background: '#0f172a', display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  },
  loadingSpinner: { fontSize: 18, color: '#94a3b8' },
  errorContainer: {
    minHeight: '100vh', background: '#0f172a', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  errorCard: {
    background: 'white', padding: 40, borderRadius: 15, textAlign: 'center',
    maxWidth: 500, boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  backButton: {
    background: '#3b82f6', color: 'white', padding: '12px 24px',
    borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600,
    fontSize: 16, textDecoration: 'none', display: 'inline-block',
  },
};

export default App;
